import { Fragment, useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { Settings2, MessageSquare, SplitSquareHorizontal, Zap, Sparkles } from 'lucide-react';
import { getSourceHandleIds, getSourceHandleLabel } from '../../utils/workflowNodeHandles';
import { getCanvasNodePreviewText, PALETTE } from '../../utils/workflowPaletteConstants';

const HANDLE_SIZE = { width: 12, height: 12 };
const HANDLE_CLASS = "border-2 border-white dark:border-slate-900 shadow-sm";
const OUTPUT_ROW_HEIGHT = 28;

const nodeStyles = {
  trigger: { bg: '#EEEDFE', border: '#AFA9EC', label: '#3C3489', icon: Zap },
  message: { bg: '#E1F5EE', border: '#9FE1CB', label: '#085041', icon: MessageSquare },
  condition: { bg: '#FAEEDA', border: '#FAC775', label: '#633806', icon: SplitSquareHorizontal },
  action: { bg: '#FAECE7', border: '#F5C4B3', label: '#712B13', icon: Settings2 },
  ai: { bg: '#EAF3DE', border: '#C0DD97', label: '#27500A', icon: Sparkles }
};

const BaseNode = ({ data, type, hasError, outputCount = 0 }) => {
  const style = nodeStyles[type];
  const Icon = style.icon;
  const extraOutputs = Math.max(0, outputCount - 1);
  const minHeight = extraOutputs > 0 ? 88 + extraOutputs * OUTPUT_ROW_HEIGHT : undefined;

  return (
    <div
      className={`rounded-xl border-2 shadow-md flex flex-col min-w-[220px] max-w-[280px] bg-white dark:bg-slate-900 transition-shadow hover:shadow-lg dark:shadow-slate-900/50 ${data.selected ? 'ring-2 ring-[#0F6E56] dark:ring-emerald-500 ring-offset-1 dark:ring-offset-slate-950' : ''} ${hasError ? 'border-red-500 ring-2 ring-red-500' : ''}`}
      style={{ borderColor: hasError ? '#ef4444' : style.border, minHeight }}
    >
      <div
        className="px-3 py-2.5 flex items-center gap-2 rounded-t-[10px] border-b"
        style={{ backgroundColor: style.bg, borderColor: style.border }}
      >
        <Icon className="w-4 h-4 shrink-0" style={{ color: style.label }} />
        <span className="text-sm font-semibold truncate" style={{ color: style.label }}>{data.label}</span>
      </div>
      <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-4">
        {getCanvasNodePreviewText(data) || 'Configure this step'}
      </div>
    </div>
  );
};

function TargetHandle({ isConnectable, className }) {
  return (
    <Handle
      type="target"
      position={Position.Left}
      isConnectable={isConnectable}
      style={HANDLE_SIZE}
      className={className}
    />
  );
}

function getHandleTopPercent(index, total) {
  return ((index + 1) / (total + 1)) * 100;
}

function SourceHandles({ id, data, isConnectable, colorClass }) {
  const handles = getSourceHandleIds(data);
  const updateNodeInternals = useUpdateNodeInternals();
  const handleSignature = handles.join('|');

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, handleSignature, updateNodeInternals]);

  if (!handles.length) return null;

  if (handles.length === 1) {
    return (
      <Handle
        type="source"
        position={Position.Right}
        id={handles[0]}
        isConnectable={isConnectable}
        style={HANDLE_SIZE}
        className={`${HANDLE_CLASS} ${colorClass}`}
      />
    );
  }

  return handles.map((handleId, index) => {
    const top = `${getHandleTopPercent(index, handles.length)}%`;

    return (
      <Fragment key={`${id}-${handleId}-${index}`}>
        <Handle
          type="source"
          position={Position.Right}
          id={handleId}
          isConnectable={isConnectable}
          style={{ ...HANDLE_SIZE, top, transform: 'translateY(-50%)' }}
          className={`${HANDLE_CLASS} ${colorClass}`}
        />
        <span
          className="absolute text-[9px] font-bold text-gray-500 dark:text-gray-400 whitespace-nowrap leading-tight pointer-events-none"
          style={{ left: 'calc(100% + 8px)', top, transform: 'translateY(-50%)' }}
        >
          {getSourceHandleLabel(handleId, data)}
        </span>
      </Fragment>
    );
  });
}

function WorkflowNode({ id, data, isConnectable, type, colorClass }) {
  const outputCount = getSourceHandleIds(data).length;
  const hasTarget = type !== 'message' || data.paletteType !== PALETTE.END;

  return (
    <>
      {hasTarget && (
        <TargetHandle isConnectable={isConnectable} className={`${HANDLE_CLASS} ${colorClass}`} />
      )}
      <BaseNode data={data} type={type} hasError={data.hasError} outputCount={outputCount} />
      <SourceHandles id={id} data={data} isConnectable={isConnectable} colorClass={colorClass} />
    </>
  );
}

export const TriggerNode = (props) => (
  <WorkflowNode {...props} type="trigger" colorClass="bg-[#AFA9EC]" />
);

export const MessageNode = (props) => (
  <WorkflowNode {...props} type="message" colorClass="bg-[#9FE1CB]" />
);

export const ConditionNode = (props) => (
  <WorkflowNode {...props} type="condition" colorClass="bg-[#FAC775]" />
);

export const ActionNode = (props) => (
  <WorkflowNode {...props} type="action" colorClass="bg-[#F5C4B3]" />
);

export const AINode = (props) => (
  <WorkflowNode {...props} type="ai" colorClass="bg-[#C0DD97]" />
);
